import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/da2lrn3d3r93mn1k3bo0";

export default function Contact() {
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 反垃圾蜜罐：命中则静默按成功处理，不发起请求
    const hp = (data.get("phone_alt") as string)?.trim();
    if (hp) {
      setSuccess(true);
      setFormError(null);
      return;
    }

    data.delete("phone_alt");

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      payload.append(key, value as string);
    });

    setSending(true);
    setFormError(null);
    setSuccess(false);

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (res.ok && parsed?.code === "OK" && !/spam/i.test(serverMsg)) {
        setSuccess(true);
        form.reset();
      } else {
        setFormError(serverMsg.includes("spam") ? "提交内容被识别为垃圾信息，请重新填写。" : (serverMsg || "提交失败，请稍后再试。"));
      }
    } catch {
      setFormError("网络异常，请稍后再试。");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <p className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-500">
              <span className="h-px w-8 bg-primary-400" aria-hidden="true"></span>
              联系我们
            </p>
            <h2 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              想补充角色信息？欢迎来信
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              无论你想分享新的感情线线索、指正角色关系图谱，还是推荐值得整理的人物专题，
              都可以通过下面的表单告诉我们。我们会认真阅读每一条留言。
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-4 rounded-2xl border border-background-200/80 bg-background-50 p-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-xl" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-600">内容合作 / 纠错反馈</p>
                  <p className="mt-0.5 text-sm font-medium text-foreground-900">lianai@example.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-background-200/80 bg-background-50 p-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-time-line text-xl" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-600">更新频率</p>
                  <p className="mt-0.5 text-sm font-medium text-foreground-900">随漫画更新持续跟进角色与CP动态</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-background-200/80 bg-background-50 p-6 md:p-8">
            {success ? (
              <div className="flex h-full flex-col items-center justify-center py-10 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-check-line text-3xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950">留言已提交</h3>
                <p className="mt-2 max-w-sm text-sm leading-7 text-foreground-700">
                  感谢你的反馈！我们会认真查看并尽快跟进。
                </p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      你的称呼
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      联系邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    留言主题
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="角色信息补充">角色信息补充</option>
                    <option value="关系图谱纠错">关系图谱纠错</option>
                    <option value="感情线梳理建议">感情线梳理建议</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="想补充或指正的内容……"
                    className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  ></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
                </div>

                {/* 反垃圾蜜罐字段 */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                {formError && (
                  <p className="rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-800" role="alert">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  className="w-full whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? "正在提交…" : "提交留言"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}